/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: Hero section for AI Image Generator - See What FalcoCut Can Create in Seconds
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface ImageData {
  imageUrl: string;
  alt: string;
}

const images: ImageData[] = [
  {
    imageUrl:
      'https://assets.falcocut.ai/ai-image-generator/showcase-product-supplement.webp',
    alt: 'AI generated product supplement image',
  },
  {
    imageUrl:
      'https://assets.falcocut.ai/ai-image-generator/showcase-man-holding-product.webp',
    alt: 'AI generated man holding product image',
  },
  {
    imageUrl:
      'https://assets.falcocut.ai/ai-image-generator/showcase-cat-food-product.webp',
    alt: 'AI generated cat food product image',
  },
];

const HeroSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          See What FalcoCut Can Create in Seconds
        </h2>

        {/* 描述 */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-8 pad:mb-10 sm:mb-12 leading-relaxed">
          FalcoCut AI Image Generator gives you the freedom to create visuals in any style
          you can imagine — from realistic portraits and product shots to anime
          characters, digital art, and stylized concepts. With simple text or photo
          prompts, you can instantly explore completely different looks without needing
          design skills.
        </p>

        {/* 图片卡片区域 */}
        <div className="relative mb-10 pad:mb-12 sm:mb-14">
          {/* 背景装饰 - 浅蓝色渐变，被内容撑开 */}
          <div
            className="absolute inset-0 rounded-[32px]"
            style={{
              background:
                'linear-gradient(180deg, rgba(224, 231, 255, 0.6) 0%, rgba(241, 245, 249, 0.8) 100%)',
            }}
          />

          {/* 图片卡片网格 - 3张图片横向排列 */}
          <div className="relative flex flex-col pad:flex-row items-center justify-center gap-4 pad:gap-6 sm:gap-8 px-4 pad:px-8 sm:px-12 py-8 pad:py-10 sm:py-12">
            {images.map((image, index) => (
              <div
                key={index}
                className="relative rounded-[16px] overflow-hidden bg-gray-100 shadow-lg w-full max-w-[360px] pad:max-w-[320px] sm:max-w-[360px] aspect-[4/3]"
              >
                <Image
                  src={image.imageUrl}
                  alt={image.alt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 360px, (max-width: 1024px) 320px, 360px"
                />
              </div>
            ))}
          </div>
        </div>

        {/* CTA 按钮 */}
        <div className="flex justify-center">
          <StartButton text="Create Your Image Now" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
